import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, XCircle, Clock, Ban, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QV_LOGO =
  "https://customer-assets.emergentagent.com/job_athletic-edge-19/artifacts/7jyn9i1w_IMG_2453.PNG";

const STATE_UI = {
  EXPIRED:   { icon: Clock,      title: "Enlace caducado",       body: "Este enlace ha caducado. Pide al entrenador que te envíe uno nuevo." },
  REVOKED:   { icon: Ban,        title: "Enlace revocado",       body: "El entrenador ha desactivado este enlace." },
  COMPLETED: { icon: CheckCircle2, title: "Diagnóstico completado", body: "Ya has enviado este diagnóstico. Gracias — el entrenador lo revisará y contactará contigo." },
};

function StateScreen({ state }) {
  const cfg = STATE_UI[state] || { icon: XCircle, title: "Enlace no disponible", body: "" };
  const Icon = cfg.icon;
  return (
    <div style={styles.stateBox} data-testid={`diag-state-${state.toLowerCase()}`}>
      <Icon size={44} color="#a3a3a3" strokeWidth={1.4} />
      <div style={styles.stateTitle}>{cfg.title}</div>
      <div style={styles.stateBody}>{cfg.body}</div>
    </div>
  );
}

function Field({ f, value, onChange }) {
  const handleChange = (raw) => {
    if (f.type === "checkbox") return onChange(f.id, raw);
    if (f.type === "number") {
      if (raw === "" || raw === null) return onChange(f.id, "");
      const n = Number(raw);
      return onChange(f.id, Number.isFinite(n) ? n : raw);
    }
    return onChange(f.id, raw);
  };
  const common = {
    style: styles.input,
    "data-testid": `diag-field-${f.id}`,
    value: value ?? "",
    onChange: (e) => handleChange(f.type === "checkbox" ? e.target.checked : e.target.value),
  };
  if (f.type === "textarea") return <textarea rows={3} {...common} style={{ ...styles.input, minHeight: 90, resize: "vertical" }} />;
  if (f.type === "select") {
    return (
      <select {...common} style={styles.input}>
        <option value="">— Selecciona —</option>
        {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === "checkbox") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#e5e5e5", fontSize: 14, cursor: "pointer" }}>
        <input type="checkbox" checked={!!value} onChange={common.onChange} data-testid={`diag-field-${f.id}`} style={{ width: 18, height: 18 }} />
        <span>{f.label}{f.required && <span style={{ color: "#ef4444" }}> *</span>}</span>
      </label>
    );
  }
  const type = f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "email" ? "email" : "text";
  return <input type={type} {...common} min={f.min} max={f.max} />;
}

export default function PublicDiagnostic() {
  const { token } = useParams();
  const [state, setState] = useState("LOADING");
  const [schema, setSchema] = useState(null);
  const [meta, setMeta] = useState({});
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/diagnostic/${encodeURIComponent(token)}`);
        setState(data.status);
        setSchema(data.schema);
        setMeta({ player_hint: data.player_hint, expires_at: data.expires_at });
      } catch (e) {
        setState("INVALID");
      }
    })();
  }, [token]);

  const onChange = (id, v) => setAnswers((p) => ({ ...p, [id]: v }));

  const requiredMissing = useMemo(() => {
    if (!schema) return [];
    const miss = [];
    for (const s of schema.sections || []) {
      for (const f of s.fields || []) {
        if (f.required) {
          const v = answers[f.id];
          if (v === undefined || v === null || v === "" || v === false) miss.push(f);
        }
      }
    }
    return miss;
  }, [schema, answers]);

  const submit = async () => {
    if (requiredMissing.length) {
      toast.error(`Faltan campos obligatorios (${requiredMissing.length})`);
      const first = document.querySelector(`[data-testid="diag-field-${requiredMissing[0].id}"]`);
      if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/diagnostic/${encodeURIComponent(token)}/submit`, { answers });
      setState("COMPLETED");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) setState("EXPIRED");
      else if (code === 404) setState("INVALID");
      else toast.error(e?.response?.data?.detail || "No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <header style={styles.header}>
          <img src={QV_LOGO} alt="QV" style={{ width: 44, height: 44, objectFit: "contain" }} />
          <div>
            <div style={styles.brandTop}>QV PERFORMANCE</div>
            <div style={styles.brandSub}>Diagnóstico Inicial</div>
          </div>
        </header>

        {state === "LOADING" && (
          <div style={styles.stateBox}><Loader2 size={32} className="qv-spin" /><div style={styles.stateBody}>Cargando…</div></div>
        )}

        {(state === "EXPIRED" || state === "REVOKED" || state === "COMPLETED" || state === "INVALID") && (
          <StateScreen state={state === "INVALID" ? "REVOKED" : state} />
        )}

        {(state === "PENDING" || state === "OPENED") && schema && (
          <>
            <div style={styles.intro}>
              <div style={styles.introTitle}>Bienvenido{meta.player_hint ? `, familia de ${meta.player_hint}` : ""}</div>
              <p style={styles.introBody}>
                Este cuestionario nos ayuda a diseñar un plan personalizado y seguro para el jugador.
                Tarda unos <b>5 minutos</b>. Ninguna respuesta se hará pública y solo el entrenador la revisará.
              </p>
              <div style={styles.trustRow}>
                <span style={styles.trustChip}><ShieldCheck size={14} /> Enlace privado</span>
                <span style={styles.trustChip}>Un solo uso</span>
                <span style={styles.trustChip}>Revisión manual del coach</span>
              </div>
            </div>

            {schema.sections.map((sec) => (
              <section key={sec.id} style={styles.section} data-testid={`diag-section-${sec.id}`}>
                <div style={styles.sectionTitle}>{sec.title}</div>
                <div style={styles.grid}>
                  {sec.fields.map((f) => (
                    <div key={f.id} style={{ gridColumn: f.type === "textarea" || f.type === "checkbox" ? "1 / -1" : "auto" }}>
                      {f.type !== "checkbox" && (
                        <label style={styles.label}>
                          {f.label}{f.required && <span style={{ color: "#ef4444" }}> *</span>}
                        </label>
                      )}
                      <Field f={f} value={answers[f.id]} onChange={onChange} />
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <div style={styles.submitBar}>
              <div style={{ color: "#a3a3a3", fontSize: 13 }}>
                {requiredMissing.length > 0
                  ? `${requiredMissing.length} campo(s) obligatorio(s) por completar`
                  : "Todo listo para enviar"}
              </div>
              <button
                onClick={submit}
                disabled={submitting || requiredMissing.length > 0}
                style={{ ...styles.submitBtn, opacity: (submitting || requiredMissing.length > 0) ? 0.5 : 1, cursor: (submitting || requiredMissing.length > 0) ? "not-allowed" : "pointer" }}
                data-testid="diag-submit-btn"
              >
                {submitting ? "Enviando…" : "Enviar diagnóstico"}
              </button>
            </div>
          </>
        )}

        <footer style={styles.footer}>
          <ShieldCheck size={12} style={{ verticalAlign: "middle", marginRight: 6 }} />
          QV Performance · Los datos se tratan de forma confidencial
        </footer>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#050505", color: "#fff", padding: "24px 16px 60px" },
  container: { maxWidth: 720, margin: "0 auto" },
  header: { display: "flex", alignItems: "center", gap: 14, paddingBottom: 24, borderBottom: "1px solid rgba(255,255,255,0.08)", marginBottom: 28 },
  brandTop: { fontFamily: "Bebas Neue, sans-serif", letterSpacing: "0.14em", fontSize: 22 },
  brandSub: { fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase", color: "#a3a3a3", marginTop: 3 },
  intro: { marginBottom: 32 },
  introTitle: { fontSize: 20, fontWeight: 600, marginBottom: 8 },
  introBody: { color: "#d4d4d4", fontSize: 14, lineHeight: 1.6 },
  trustRow: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 },
  trustChip: { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 999, fontSize: 11, color: "#d4d4d4", letterSpacing: "0.06em" },
  section: { marginTop: 20, padding: "22px 20px", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4 },
  sectionTitle: { fontFamily: "Bebas Neue, sans-serif", letterSpacing: "0.14em", fontSize: 20, marginBottom: 18, color: "#dc2626" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 },
  label: { display: "block", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "#a3a3a3", marginBottom: 6 },
  input: { width: "100%", background: "#111", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "10px 12px", fontSize: 14, borderRadius: 3, outline: "none", boxSizing: "border-box" },
  submitBar: { position: "sticky", bottom: 0, marginTop: 24, padding: "16px 20px", background: "rgba(5,5,5,0.94)", backdropFilter: "blur(10px)", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  submitBtn: { background: "#dc2626", color: "#fff", border: "none", padding: "12px 24px", fontWeight: 700, fontSize: 14, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", borderRadius: 2 },
  stateBox: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 24px", textAlign: "center", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 4, marginTop: 20, gap: 16 },
  stateTitle: { fontSize: 20, fontWeight: 600 },
  stateBody: { color: "#a3a3a3", fontSize: 14, maxWidth: 420, lineHeight: 1.6 },
  footer: { marginTop: 32, textAlign: "center", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#525252" },
};
